<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";
import AmountInput from "../../../../components/ui/form/AmountInput.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  code: props.data?.code || "",
  name: props.data?.name || props.listCreateName || "",
  percentage: props.data?.percentage || 0,
  active: props.data?.active || true,
  tax_payable_account_id: props.data?.tax_payable_account_id || "",
  tax_receivable_account_id: props.data?.tax_receivable_account_id || "",
  uid: props.data?.uid || "",
});

setFieldRules("code", [required("Code is required")]);
setFieldRules("name", [required("Tax name is required")]);
setFieldRules("tax_payable_account_id", [
  required("Tax Payable Account is required"),
]);
setFieldRules("tax_receivable_account_id", [
  required("Tax Receivable Account is required"),
]);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        setFormData(response.data);
      }
    }
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      setFormData(props.data)
    }
})

</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <div class="grid md:grid-cols-2 gap-6">
        <TextInput
          v-model="fields.code.value"
          label="Code"
          placeholder="Enter Tax Code"
          :readonly="isReadOnly"
          :error="fields.code.touched ? fields.code.error : null"
          @blur="fields.code.touched = true"
        />

        <TextInput
          v-model="fields.name.value"
          label="Name"
          placeholder="Enter Tax Name"
          :readonly="isReadOnly"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />

        <AmountInput
          v-model="fields.percentage.value"
          label="Tax Rate (%)"
          :readonly="isReadOnly"
          :error="fields.percentage.touched ? fields.percentage.error : null"
          @blur="fields.percentage.touched = true"
        />

        <SearchSelect
          v-model="fields.tax_payable_account_id.value"
          end-point="finance/accounting/chart-of-accounts"
          label="Tax Payable Account"
          :allow-new-record="true"
          :readonly="isReadOnly"
          :error="
            fields.tax_payable_account_id.touched
              ? fields.tax_payable_account_id.error
              : null
          "
          @blur="fields.tax_payable_account_id.touched = true"
        />

        <SearchSelect
          v-model="fields.tax_receivable_account_id.value"
          end-point="finance/accounting/chart-of-accounts"
          label="Tax Receivable Account"
          :allow-new-record="true"
          :readonly="isReadOnly"
          :error="
            fields.tax_receivable_account_id.touched
              ? fields.tax_receivable_account_id.error
              : null
          "
          @blur="fields.tax_receivable_account_id.touched = true"
        />

        <CheckInput
          v-model="fields.active.value"
          :readonly="isReadOnly"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>
    </template>
  </ModalFormTemplate>
</template>
